import React, { useCallback, useState } from 'react';
import { useRouter } from '@Helpers/useRouter';
import { useTranslation } from 'react-i18next';
import { useArchiveFund } from '@Hooks/Api/useArchiveFund';
import { useNotificationsContext } from '@Contexts';
import * as Sentry from '@sentry/react';
import { RoutingPaths } from '@App/paths';
import { ConfirmModal } from '@Components';

export interface ArchiveFundModalProps {
  fundId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ArchiveFundModal = ({ fundId, isOpen, onClose, onSuccess }: ArchiveFundModalProps) => {
  const { t } = useTranslation('dashboard');
  const [archiveProject] = useArchiveFund();
  const { success: successNotification, error: errorNotification } = useNotificationsContext();
  const { push } = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const confirmArchive = useCallback(async () => {
    setIsLoading(true);
    try {
      await archiveProject(fundId);
      successNotification(t('funder.archive_fund_success'));

      if (onSuccess) {
        onSuccess();
      } else {
        push(RoutingPaths.DASHBOARD_GROUP);
      }

      onClose();
    } catch (e: any) {
      Sentry.captureException(
        new Error(
          `Archiving fund ${fundId} failed. Error: ${
            e?.data?.detail ? e.data.detail.join(' ') : 'Unknown error'
          }`,
        ),
      );
      errorNotification(t('funder.archive_fund_fail'));
    } finally {
      setIsLoading(false);
    }
  }, [archiveProject, fundId, successNotification, errorNotification, t, onSuccess, push, onClose]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('funder.archive_modal.title')}
      cancelText={t('funder.archive_modal.cancel')}
      onCancel={onClose}
      confirmText={t('funder.archive_modal.confirm')}
      onConfirm={confirmArchive}
      isConfirmBtnDanger
    >
      {t('funder.archive_modal.text')}
    </ConfirmModal>
  );
};
